"use client";

import React from "react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { ShieldCheck, ArrowRight, Palette, Globe, EyeOff, Briefcase, Layers3, FileText } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
const highlights = [{
  icon: Palette,
  title: "Branded client delivery",
  text: "Apply your own logo, colors, and client-facing presentation."
}, {
  icon: Globe,
  title: "Custom routing",
  text: "Deliver through branded domains and white-label environments."
}, {
  icon: EyeOff,
  title: "Invisible infrastructure",
  text: "EloRAG stays behind the scenes while you own the client relationship."
}];
export default function YourBrandOurGovernance() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: visualRef,
    isVisible: isVisualVisible
  } = useRevealOnScroll({
    threshold: 0.15
  });
  return <section id="your-brand-our-governance" className="relative overflow-hidden bg-background py-14 lg:py-18">
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -top-[18%] -right-[10%] h-[45%] w-[45%] rounded-full bg-primary/6 blur-[120px]" />
        <div className="absolute top-[58%] -left-[10%] h-[35%] w-[35%] rounded-full bg-primary/5 blur-[100px]" />
      </div>

      <div className="relative z-10 mx-auto max-w-7xl px-6 lg:px-8">
        <div className="grid items-center gap-10 lg:grid-cols-12 lg:gap-10">
          <div ref={headerRef} className={cn("lg:col-span-7 transition-all duration-700 ease-out", isHeaderVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0")}>
            <div className="mb-5 inline-flex items-center gap-2 rounded-full bg-primary/10 px-3 py-1 text-sm font-medium text-primary">
              <Layers3 className="size-4" />
              <span>White-label for agencies</span>
            </div>

            <h1 className="max-w-4xl text-4xl font-bold tracking-tight text-foreground md:text-5xl lg:text-[3.45rem] leading-[1.02]">
              Resell governed RAG under your own brand
            </h1>

            <p className="mt-5 max-w-2xl text-lg leading-relaxed text-muted-foreground md:text-[1.1rem]">
              Deliver branded AI environments to clients while EloRAG handles the governance model,
              approved-only retrieval, citation-based answers, and multi-tenant control behind the scenes.
            </p>

            <div className="mt-7 flex flex-wrap items-center gap-3">
              <Button asChild size="lg" className="h-12 px-8 text-base shadow-sm">
                <Link to="/BookADemoContactSalesPage">
                  Book a White-Label Demo
                  <ArrowRight className="ml-2 size-4" />
                </Link>
              </Button>

              <Button asChild variant="outline" size="lg" className="h-12 bg-background/60 px-8 text-base backdrop-blur-sm">
                <Link to="/SecurityAndGovernancePage">
                  Explore Governance Features
                </Link>
              </Button>
            </div>

            <div className="mt-7 flex flex-wrap items-center gap-x-6 gap-y-3 text-sm font-medium text-muted-foreground">
              <span>Branded client experience</span>
              <span className="hidden sm:inline text-border">•</span>
              <span>Governance stays intact</span>
              <span className="hidden sm:inline text-border">•</span>
              <span>Multi-tenant delivery</span>
              <span className="hidden sm:inline text-border">•</span>
              <span>EloRAG remains behind the scenes</span>
            </div>

            <div className="mt-7 inline-flex rounded-full border border-border/70 bg-secondary/35 px-4 py-2 text-sm font-semibold text-foreground">
              Best suited for agencies managing multiple client environments
            </div>
          </div>

          <div ref={visualRef} className={cn("lg:col-span-5 transition-all duration-1000 delay-150 ease-out", isVisualVisible ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0")}>
            <Card className="rounded-2xl border border-border/70 bg-card/95 p-6 shadow-[0_24px_60px_-24px_rgba(0,0,0,0.16)] backdrop-blur-xl">
              <div className="mb-5 flex items-center justify-between">
                <div>
                  <div className="text-sm font-semibold uppercase tracking-wider text-primary">
                    White-label model
                  </div>
                  <div className="mt-1 text-xl font-semibold text-foreground">
                    Your brand. EloRAG governance.
                  </div>
                </div>
                <div className="rounded-xl bg-primary/10 p-3">
                  <Briefcase className="size-6 text-primary" />
                </div>
              </div>

              <div className="space-y-4">
                <div className="rounded-xl border border-border/70 bg-secondary/25 p-4">
                  <div className="flex items-start gap-3">
                    <div className="rounded-lg bg-primary/10 p-2 shrink-0">
                      <Layers3 className="size-4 text-primary" />
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-foreground">
                        Client-facing layer
                      </div>
                      <div className="mt-1 text-sm leading-relaxed text-muted-foreground">
                        Your clients see your identity, your branded environment, and your delivery model.
                      </div>
                    </div>
                  </div>
                </div>

                <div className="rounded-xl border border-border/70 bg-secondary/25 p-4">
                  <div className="flex items-start gap-3">
                    <div className="rounded-lg bg-primary/10 p-2 shrink-0">
                      <FileText className="size-4 text-primary" />
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-foreground">
                        EloRAG governance layer
                      </div>
                      <div className="mt-1 text-sm leading-relaxed text-muted-foreground">
                        Approved-only retrieval, citation-based answers, refusal behavior, and tenant isolation remain enforced underneath.
                      </div>
                    </div>
                  </div>
                </div>

                <div className="rounded-xl border border-primary/20 bg-primary/[0.04] p-4">
                  <div className="text-sm font-semibold text-foreground">
                    Commercial fit
                  </div>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                    You keep the client relationship and branded positioning, while EloRAG handles the harder control layer required for trusted AI delivery.
                  </p>
                </div>
              </div>
            </Card>
          </div>
        </div>

        <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-3">
          {highlights.map((item, i) => {
          const Icon = item.icon;
          return <Card key={item.title} data-index={i} className="rounded-2xl border border-border/70 bg-card p-6 shadow-[0_10px_30px_-18px_rgba(0,0,0,0.12)] transition-all duration-300 hover:border-primary/25 hover:shadow-[0_18px_40px_-18px_rgba(0,0,0,0.15)]">
                <div className="mb-4 rounded-xl bg-primary/10 p-3 w-fit">
                  <Icon className="size-5 text-primary" />
                </div>
                <h3 className="mb-2 text-lg font-semibold text-foreground">{item.title}</h3>
                <p className="text-sm leading-relaxed text-muted-foreground">{item.text}</p>
              </Card>;
        })}
        </div>
      </div>
    </section>;
}